import { useGetAllTeamKPIQuery } from "@/app/features/employee/performance/performance.api";
import CustomTable from "@/components/CustomTable";
import StatusTag from "@/components/StatusTag";
import { FILTER_KEYS } from "@/constants/keys.constants";
import { useAppFilters } from "@/hooks";
import useAuth from "@/hooks/useAuth";
import useDiscloser from "@/hooks/useDiscloser";
import { EyeIcon, LaptopPerformanceIcon } from "@/icons";
import { formatDate, normalizeColumnValue } from "@/utils";
import { countParPageSerialNumber } from "@/utils/global.utils";
import { Button, Flex, type TableProps } from "antd";
import { useState } from "react";
import BulkApproveKPIDrawer from "./BulkApproveKPIDrawer";
import LogViewer from "./LogViewer";
import ManageOwnKPI from "./ManageOwnKpi";
import ViewOwnKPI from "./ViewOwnKPI";

// Statuses that can be bulk approved by the manager
const APPROVABLE_STATUSES = ["PENDING", "SUBMITTED", "DRAFT"];

const TEAM_KPI_STATUS_CONFIG = [
  {
    label: "Not Started",
    value: "NOT_STARTED",
    className: "status status-slate",
  },
  {
    label: "Pending",
    value: "PENDING",
    className: "status status-yellow",
  },
  {
    label: "Draft",
    value: "DRAFT",
    className: "status status-yellow",
  },
  {
    label: "Returned",
    value: "RETURNED",
    className: "status status-fuchsia",
  },
  {
    label: "Approved",
    value: "APPROVED",
    className: "status status-success",
  },
  {
    label: "Submitted",
    value: "SUBMITTED",
    className: "status status-sky",
  },
  {
    label: "Rejected",
    value: "REJECTED",
    className: "status status-error",
  },
];

const TeamKPI = () => {
  const { userId, organizationId } = useAuth();
  const { page, limit, handlePagination, filters } = useAppFilters(
    FILTER_KEYS.LM_TEAM_KPI,
  );
  const [config, handleOpen, handleClose] = useDiscloser<"VIEW" | "MANAGE">();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);

  const { data, isFetching } = useGetAllTeamKPIQuery({
    organization_id: organizationId,
    manager_id: userId,
    page,
    limit,
    ...filters,
  });

  const teamKpis = data?.data?.employees || [];
  const total = data?.data?.pagination?.total || 0;

  const rowSelection: TableProps<any>["rowSelection"] = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys as string[]),
    getCheckboxProps: (record) => ({
      disabled: !APPROVABLE_STATUSES.includes(record?.kpi_approval_status),
    }),
  };

  const handleBulkApproveSuccess = () => {
    setSelectedRowKeys([]);
  };

  const columns: TableProps<any>["columns"] = [
    {
      title: "SL. No.",
      align: "center",
      width: 80,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Plan Name",
      dataIndex: "plan_name",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Employee ID",
      dataIndex: ["employee", "employee_id"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Employee Name",
      dataIndex: ["employee", "name"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Designation",
      dataIndex: ["employee", "designation"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Plan Period",
      dataIndex: "plan_period",
      align: "center",
      render: (val: string) => normalizeColumnValue(val),
    },
    {
      title: "Plan Type",
      dataIndex: "plan_type",
      align: "center",
      render: (val: string) => normalizeColumnValue(val),
    },
    {
      title: "Status",
      dataIndex: "kpi_approval_status",
      align: "center",
      render: (val: string) => (
        <StatusTag
          value={val}
          shouldCapitalize={false}
          config={TEAM_KPI_STATUS_CONFIG}
        />
      ),
    },
    {
      width: 150,
      title: "Actions",
      render: (record: any) => (
        <Flex gap={8}>
          <Button
            size="small"
            className="action-btn"
            icon={<EyeIcon size={20} />}
            disabled={
              record?.kpi_approval_status !== "PENDING" &&
              record?.kpi_approval_status !== "SUBMITTED"&&
              record?.kpi_approval_status !== "DRAFT"
            }
            onClick={() =>
              handleOpen({
                type: "MANAGE",
                data: {
                  planName: record?.plan_name,
                  mappingId: record?.mapping_id,
                  planId: record?.plan_id,
                  employeeId: record?.employee_id,
                  status: record?.kpi_approval_status,
                },
              })
            }
          />
          <Button
            size="small"
            type="default"
            className="btn-primary-bordered h-8"
            onClick={() =>
              handleOpen({
                type: "VIEW",
                data: {
                  planName: record?.plan_name,
                  mappingId: record?.mapping_id,
                  dateTime: formatDate(record?.created_at),
                },
              })
            }
          >
            <LaptopPerformanceIcon className="text-theme-primary size-4" /> Log
            View
          </Button>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={teamKpis}
        rowKey="mapping_id"
        rowSelection={rowSelection}
        onPaginate={handlePagination}
        customTitle="Team Member KPI List"
        tableRootClassName="border-0 p-0"
        loading={isFetching}
        total={total}
        filterSection={
          selectedRowKeys.length > 0 ? (
            <Button
              size="large"
              className="btn-primary min-w-24"
              onClick={() => setBulkApproveOpen(true)}
            >
              Approve ({selectedRowKeys.length})
            </Button>
          ) : (
            <></>
          )
        }
      />

      <BulkApproveKPIDrawer
        open={bulkApproveOpen}
        onClose={() => setBulkApproveOpen(false)}
        mappingIds={selectedRowKeys}
        onSuccess={handleBulkApproveSuccess}
      />

      <LogViewer
        open={config.open && config.type === "VIEW"}
        onClose={handleClose}
        id={config.data?.mappingId}
        planName={config.data?.planName}
        dateTime={config.data?.dateTime}
      />

      {config.data?.status === "SUBMITTED" ? (
        <ViewOwnKPI
          open={config.open && config.type === "MANAGE"}
          onClose={handleClose}
          planName={config.data?.planName}
          mappingId={config.data?.mappingId}
        />
      ) : (
        <ManageOwnKPI
          open={config.open && config.type === "MANAGE"}
          onClose={handleClose}
          planName={config.data?.planName}
          mappingId={config.data?.mappingId}
          planId={config.data?.planId}
          employeeId={config.data?.employeeId}
          source="TEAM"
          mode="edit"
        />
      )}
    </>
  );
};

export default TeamKPI;
